'use client';

import React, { useState, useEffect, useMemo, useId } from 'react';
import dynamic from 'next/dynamic';
import { components, InputProps, GroupBase } from 'react-select';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';

const Select = dynamic(() => import('react-select'), { ssr: false });

// Custom Input component to disable browser autocomplete
const NoAutoCompleteInput = (props: InputProps<any, boolean, GroupBase<any>>) => {
  const inputId = useId();
  return (
    <components.Input
      {...props}
      autoComplete="off"
      aria-autocomplete="none"
      data-lpignore="true"
      data-form-type="other"
      name={`no-autofill-${inputId}`}
    />
  );
};

export interface Country {
  id: number;
  code: string;
  code3?: string;
  name: string;
  name_translations?: Record<string, string>;
  phone_code?: string;
  currency_code?: string;
}

export interface City {
  id: number;
  country_id: number;
  code?: string;
  name: string;
  name_translations?: Record<string, string>;
  latitude?: number;
  longitude?: number;
}

interface SelectOption {
  value: number;
  label: string;
  code?: string;
}

interface CountryCitySelectProps {
  countries: Country[];
  countryValue?: string | number | null;
  cityValue?: string | number | null;
  onCountryChange?: (countryId: number | null, countryCode: string | null) => void;
  onCityChange?: (cityId: number | null, cityName?: string | null) => void;
  countryPlaceholder?: string;
  cityPlaceholder?: string;
  countryLabel?: string;
  cityLabel?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
}

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderColor: state.isFocused ? 'rgba(0,0,0,0.40)' : 'rgba(0,0,0,0.08)',
    borderRadius: '0.375rem',
    padding: '0.125rem 0.25rem',
    minHeight: '32px',
    boxShadow: 'none',
    fontSize: '0.75rem',
    '&:hover': {
      borderColor: 'rgba(0,0,0,0.15)',
    },
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? '#E31E24' : state.isFocused ? '#fef2f2' : 'white',
    color: state.isSelected ? 'white' : '#4F545A',
    fontSize: '0.75rem',
    padding: '6px 12px',
    '&:active': {
      backgroundColor: '#E31E24',
    },
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#9CA3AF',
    fontSize: '0.75rem',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#4F545A',
    fontSize: '0.75rem',
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    padding: '4px',
  }),
};

export default function CountryCitySelect({
  countries,
  countryValue,
  cityValue,
  onCountryChange,
  onCityChange,
  countryPlaceholder,
  cityPlaceholder,
  countryLabel,
  cityLabel,
  className = '',
  disabled = false,
  required = false,
}: CountryCitySelectProps) {
  const { getVariable } = useLanguageVariables();
  const [cities, setCities] = useState<City[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<SelectOption | null>(null);
  const [selectedCity, setSelectedCity] = useState<SelectOption | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const effectiveCountryPlaceholder = countryPlaceholder || getVariable('customer_panel.profile.select_country', 'Select Country');
  const effectiveCityPlaceholder = cityPlaceholder || getVariable('customer_panel.profile.select_city', 'Select City');
  const effectiveCountryLabel = countryLabel || getVariable('customer_panel.profile.country', 'Country');
  const effectiveCityLabel = cityLabel || getVariable('customer_panel.profile.city', 'City');

  const countryOptions: SelectOption[] = useMemo(() => {
    return countries.map((c) => ({
      value: c.id,
      label: c.phone_code ? `${c.name} (${c.phone_code})` : c.name,
      code: c.code,
    }));
  }, [countries]);

  const cityOptions: SelectOption[] = useMemo(() => {
    return cities.map((c) => ({
      value: c.id,
      label: c.name,
    }));
  }, [cities]);

  useEffect(() => {
    if (countryValue && countries.length > 0) {
      const country = countries.find((c) => 
        c.id === Number(countryValue) || c.code === countryValue
      );
      if (country) {
        setSelectedCountry({
          value: country.id,
          label: country.phone_code ? `${country.name} (${country.phone_code})` : country.name,
          code: country.code,
        });
      }
    }
  }, [countryValue, countries]);

  useEffect(() => {
    if (!selectedCountry) {
      setCities([]);
      setSelectedCity(null);
      return;
    }

    const loadCities = async () => {
      setLoadingCities(true);
      try {
        const language = getVariable('language', 'en');
        const params = new URLSearchParams({ 
          language,
          country_id: selectedCountry.value.toString(),
        });

        const response = await fetch(`/api/cities?${params.toString()}`, {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          const citiesList = Array.isArray(data.data) ? data.data : (data.data?.data || []);
          setCities(citiesList);

          if (cityValue) {
            const city = citiesList.find((c: City) => 
              c.id === Number(cityValue) || c.name === cityValue
            );
            if (city) {
              setSelectedCity({ value: city.id, label: city.name });
            }
          }
        }
      } catch (error) {
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };

    loadCities();
  }, [selectedCountry, getVariable, cityValue]);

  const handleCountryChange = (option: SelectOption | null) => {
    setSelectedCountry(option);
    setSelectedCity(null);
    setCities([]);
    onCountryChange?.(option?.value || null, option?.code || null);
  };

  const handleCityChange = (option: SelectOption | null) => {
    setSelectedCity(option);
    onCityChange?.(option?.value || null, option?.label || null);
  };

  if (!mounted) {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${className}`}>
        <div className="flex flex-col gap-5">
          <label className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
            {effectiveCountryLabel}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="h-[52px] border border-[rgba(0,0,0,0.08)] rounded-lg bg-gray-50 animate-pulse" />
        </div>
        <div className="flex flex-col gap-5">
          <label className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
            {effectiveCityLabel}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="h-[52px] border border-[rgba(0,0,0,0.08)] rounded-lg bg-gray-50 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${className}`}>
      <div className="flex flex-col gap-1.5">
        <label className="font-medium text-xs text-gray-600">
          {effectiveCountryLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <Select
          instanceId="country-select"
          value={selectedCountry}
          onChange={handleCountryChange as any}
          options={countryOptions}
          placeholder={effectiveCountryPlaceholder}
          isDisabled={disabled}
          isSearchable
          isClearable
          styles={selectStyles}
          noOptionsMessage={() => getVariable('customer_panel.common.no_options', 'No options')}
          loadingMessage={() => getVariable('customer_panel.common.loading', 'Loading...')}
          components={{ Input: NoAutoCompleteInput }}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="font-medium text-xs text-gray-600">
          {effectiveCityLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <Select
          instanceId="city-select"
          value={selectedCity}
          onChange={handleCityChange as any}
          options={cityOptions}
          placeholder={effectiveCityPlaceholder}
          isDisabled={disabled || loadingCities || !selectedCountry}
          isSearchable
          isClearable
          isLoading={loadingCities}
          styles={selectStyles}
          noOptionsMessage={() => 
            !selectedCountry 
              ? getVariable('customer_panel.profile.select_country_first', 'Please select a country first')
              : getVariable('customer_panel.common.no_options', 'No options')
          }
          loadingMessage={() => getVariable('customer_panel.common.loading', 'Loading...')}
          components={{ Input: NoAutoCompleteInput }}
        />
      </div>
    </div>
  );
}
